/**
 * BaseRepository - Clase base con lógica compartida para repositorios
 *
 * Contiene:
 * - Emisión de eventos de sincronización
 * - Métodos CRUD base
 * - Métodos bulk
 * - Soft delete (opcional)
 * - Helpers comunes (UUID, timestamp, profile)
 *
 * Los repositorios específicos extienden esta clase para reutilizar la lógica común.
 */

import {
  emitInsert,
  emitUpdate,
  emitDelete,
  emitSoftDelete,
  emitRestore,
  emitBulkInsert,
  emitBulkDelete,
  emitBulkUpdate,
  emitLocalInsert,
  emitLocalUpdate,
  emitLocalDelete
} from '../syncHelpers';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';
import { IRepository, IBulkRepository, ISoftDeleteRepository } from '../interfaces/IRepository';
import { localRepositoryEvents } from '../RepositoryChangeEventEmitter';

/**
 * Clase base abstracta para repositorios
 *
 * @template T - Tipo de la entidad
 * @template TId - Tipo del ID (por defecto string)
 */
export abstract class BaseRepository<T, TId = string> implements IRepository<T, TId>, IBulkRepository<T> {
  /**
   * Caché del perfil actual para evitar consultas repetidas
   */
  // Caché del profile actual COMPARTIDO entre todas las instancias de
  // repositorio. Antes era por instancia: cada repo hacía su propia lectura de
  // `profiles` al vencer su TTL (una navegación que toca N repos = N lecturas
  // idénticas). Compartido, toda la app hace 1 lectura por ventana de TTL.
  // La invalidación por cambio de perfil (switchCompany → saveProfile →
  // evento 'profiles' en localRepositoryEvents) la escucha cada instancia en
  // el constructor e invalida este caché global.
  private static sharedProfileCache: any = null;
  private static sharedProfileCacheTime: number = 0;
  private static readonly PROFILE_CACHE_TTL = 5000; // 5 segundos

  /**
   * Indica si este repositorio soporta soft delete
   * Cuando es true, getAll() filtra por is_deleted=false
   */
  protected supportsSoftDelete = false;

  /**
   * Indica si update() debe agregar updated_at automáticamente
   */
  protected autoTimestamp = false;

  constructor(
    protected readonly db: IDatabaseAdapter,
    protected readonly tableName: string,
    protected readonly supabaseTableName: string | null = null // null = no sincronizar
  ) {
    // Invalidar el caché de profile cuando la tabla 'profiles' cambie (ej. cambio
    // de empresa activa en switchCompany → saveProfile). Sin esto, el caché
    // (TTL 5s) devolvería un empresa_activa_id desactualizado y
    // getAllForActiveEmpresa() filtraría los datos recién descargados de la nueva
    // empresa, dejando los listados vacíos hasta que el caché expirara.
    localRepositoryEvents.subscribe((event) => {
      if (event.table === 'profiles') {
        this.invalidateProfileCache();
      }
    });
  }

  /**
   * Invalida el caché del profile actual. La próxima lectura de
   * getCurrentProfile() irá a la base de datos local.
   */
  invalidateProfileCache(): void {
    BaseRepository.sharedProfileCache = null;
    BaseRepository.sharedProfileCacheTime = 0;
  }

  // ==================== Emisión de Eventos de Sync ====================

  /**
   * Emite un evento de sincronización con Supabase
   */
  protected emitSync(action: 'INSERT' | 'UPDATE' | 'DELETE' | 'SOFT_DELETE' | 'RESTORE', data: T | TId): void {
    if (!this.supabaseTableName) return;

    switch (action) {
      case 'INSERT':
        emitInsert(this.supabaseTableName, data as unknown as Record<string, unknown>);
        break;
      case 'UPDATE':
        emitUpdate(this.supabaseTableName, data as unknown as Record<string, unknown>);
        break;
      case 'DELETE':
        emitDelete(this.supabaseTableName, data as string);
        break;
      case 'SOFT_DELETE':
        emitSoftDelete(this.supabaseTableName, data as unknown as Record<string, unknown>);
        break;
      case 'RESTORE':
        emitRestore(this.supabaseTableName, data as unknown as Record<string, unknown>);
        break;
    }
  }

  /**
   * Emite un ÚNICO evento de sincronización bulk con Supabase (BULK_INSERT / BULK_DELETE / BULK_UPDATE).
   * A diferencia de emitSync (uno por item), esto envía toda la lista en una sola operación.
   */
  protected emitBulkSync(action: 'BULK_INSERT' | 'BULK_DELETE' | 'BULK_UPDATE', items: T[] | string[]): void {
    if (!this.supabaseTableName) return;

    if (action === 'BULK_INSERT') {
      emitBulkInsert(this.supabaseTableName, items as Record<string, unknown>[]);
    } else if (action === 'BULK_DELETE') {
      emitBulkDelete(this.supabaseTableName, items as string[]);
    } else if (action === 'BULK_UPDATE') {
      emitBulkUpdate(this.supabaseTableName, items as Record<string, unknown>[]);
    }
  }

  /**
   * Emite un evento local (sin sincronizar con Supabase)
   */
  protected emitLocalSync(action: 'INSERT' | 'UPDATE' | 'DELETE', data: T | TId): void {
    if (!this.tableName) return;

    switch (action) {
      case 'INSERT':
        emitLocalInsert(this.tableName, data as unknown as Record<string, unknown>);
        break;
      case 'UPDATE':
        emitLocalUpdate(this.tableName, data as unknown as Record<string, unknown>);
        break;
      case 'DELETE':
        emitLocalDelete(this.tableName, data as string);
        break;
    }
  }

  // ==================== Métodos CRUD Base ====================

  /**
   * Obtiene una entidad por su ID
   */
  async get(id: TId): Promise<T | undefined> {
    return this.db.get<T>(this.tableName, id as string);
  }

  /**
   * Obtiene todas las entidades de la tabla
   * Si supportsSoftDelete es true, filtra los eliminados lógicamente
   */
  async getAll(): Promise<T[]> {
    if (this.supportsSoftDelete) {
      return this.db.query<T>(this.tableName, {
        field: 'is_deleted',
        operator: 'equals',
        value: false
      });
    }
    return this.db.getAll<T>(this.tableName);
  }

  /**
   * Cuenta el total de entidades en la tabla
   */
  async count(): Promise<number> {
    return this.db.count(this.tableName);
  }

  /**
   * Campo PK de la entidad. Las subclases con PK distinto a 'id'
   * (p.ej. AnimalRepository='caravana', OperacionCompraVentaRepository='uuid')
   * lo sobrescriben.
   */
  protected get pkField(): string {
    return 'id';
  }

  /**
   * Borra SILENCIOSAMENTE (sin disparar sync push) los registros cuyo
   * `empresa_id` NO está en `allowedEmpresaIds`. Los registros sin
   * `empresa_id` (datos legacy) se consideran no-autorizados y también se
   * borran (criterio conservador).
   *
   * Usa `bulkDeleteSilent` (evento local únicamente) para que el purge local
   * NO se propague como BULK_DELETE a Supabase. Devuelve la cantidad borrada.
   *
   * Lee directo del adapter (sin filtrar soft-deletes) para limpiar también
   * los registros marcados como is_deleted.
   */
  async deleteWhereEmpresaNotInSilent(allowedEmpresaIds: string[]): Promise<number> {
    const allowed = new Set(allowedEmpresaIds);
    const all = await this.db.getAll<T>(this.tableName);
    const toDelete: TId[] = [];
    for (const rec of all) {
      const emp = (rec as any)?.empresa_id;
      if (typeof emp !== 'string' || emp === '' || !allowed.has(emp)) {
        const id = (rec as any)?.[this.pkField];
        if (id !== undefined && id !== null) toDelete.push(id);
      }
    }
    if (toDelete.length === 0) return 0;
    await this.bulkDeleteSilent(toDelete);
    return toDelete.length;
  }

  /**
   * Devuelve los registros pertenecientes a la empresa activa actual (leída
   * del profile local). Si no hay empresa activa disponible, cae a `getAll()`.
   *
   * Es el método que DEBEN usar los flujos de lectura de UI para no mezclar
   * datos de distintas empresas. El sync y el purge NO lo usan (necesitan
   * ver todo).
   */
  async getAllForActiveEmpresa(): Promise<T[]> {
    const profile = await this.getCurrentProfile();
    const empresaId = profile?.empresa_activa_id;
    if (!empresaId) return this.getAll();
    const all = await this.getAll();
    return all.filter(rec => (rec as any)?.empresa_id === empresaId);
  }

  /**
   * Limpia todas las entidades de la tabla
   */
  async clear(): Promise<void> {
    await this.db.clear(this.tableName);
    this.emitLocalSync('UPDATE', { _cleared: true } as any);
  }

  // ==================== Métodos Bulk ====================

  /**
   * Inserta múltiples entidades
   *
   * Emite un ÚNICO evento BULK_INSERT (en lugar de N eventos INSERT) para que
   * SyncManager haga una sola operación bulk en Supabase.
   */
  async bulkInsert(items: T[]): Promise<void> {
    await this.db.bulkInsert<T>(this.tableName, items);

    if (this.supabaseTableName && items.length > 0) {
      this.emitBulkSync('BULK_INSERT', items);
    }
  }

  /**
   * Inserta múltiples entidades sin emitir eventos de sync (para datos desde remote)
   */
  async bulkInsertSilent(items: T[]): Promise<void> {
    await this.db.bulkInsert<T>(this.tableName, items);

    items.forEach(item => {
      this.emitLocalSync('INSERT', item);
    });
  }

  /**
   * Upsert múltiples entidades (inserta o actualiza)
   *
   * Emite un ÚNICO evento BULK_INSERT (en lugar de N eventos UPDATE) para que
   * SyncManager haga una sola operación bulk (upsert) en Supabase.
   */
  async bulkUpsert(items: T[]): Promise<void> {
    await this.db.bulkUpsert<T>(this.tableName, items);

    if (this.supabaseTableName && items.length > 0) {
      this.emitBulkSync('BULK_INSERT', items);
    }
  }

  /**
   * Upsert múltiples entidades sin emitir eventos de sync (para datos desde remote)
   */
  async bulkUpsertSilent(items: T[]): Promise<void> {
    await this.db.bulkUpsert<T>(this.tableName, items);

    items.forEach(item => {
      this.emitLocalSync('UPDATE', item);
    });
  }

  /**
   * Elimina múltiples entidades sin emitir eventos de sync
   */
  async bulkDeleteSilent(ids: TId[]): Promise<void> {
    await this.db.bulkDelete(this.tableName, ids as string[]);
    ids.forEach(id => {
      this.emitLocalSync('DELETE', id);
    });
  }

  // ==================== Alias para IBulkRepository ====================
  // Estos métodos son alias para cumplir con la interfaz IBulkRepository

  /** @alias bulkInsert */
  async bulkAdd(items: T[]): Promise<void> {
    return this.bulkInsert(items);
  }

  /** @alias bulkInsertSilent */
  async bulkAddSilent(items: T[]): Promise<void> {
    return this.bulkInsertSilent(items);
  }

  /** @alias bulkUpsert */
  async bulkPut(items: T[]): Promise<void> {
    return this.bulkUpsert(items);
  }

  /** @alias bulkUpsertSilent */
  async bulkPutSilent(items: T[]): Promise<void> {
    return this.bulkUpsertSilent(items);
  }

  /**
   * Actualiza múltiples entidades parciales emitiendo un ÚNICO evento BULK_UPDATE.
   *
   * Una sola operación local (merge parcial por PK vía this.db.bulkUpdate) y un
   * solo evento de sync, en lugar de N llamadas a this.update().
   */
  async bulkUpdate(items: Partial<T>[]): Promise<void> {
    const pkItems = items.filter(item => {
      const id = (item as any).id ?? (item as any).uuid;
      return id !== undefined && id !== null;
    });
    if (pkItems.length === 0) return;

    const records = this.autoTimestamp
      ? pkItems.map(item => ({ ...item, updated_at: this.getCurrentTimestamp() }))
      : pkItems;

    await this.db.bulkUpdate(this.tableName, records as Partial<T>[]);

    if (this.supabaseTableName) {
      this.emitBulkSync('BULK_UPDATE', records as unknown as T[]);
    }
  }

  /** Implementación de bulkDelete para IBulkRepository
   *
   * Emite un ÚNICO evento BULK_DELETE (en lugar de N eventos DELETE) para que
   * SyncManager haga una sola operación bulk (.in(pk, ids)) en Supabase.
   */
  async bulkDelete(ids: string[]): Promise<void> {
    await this.db.bulkDelete(this.tableName, ids);

    if (this.supabaseTableName && ids.length > 0) {
      this.emitBulkSync('BULK_DELETE', ids);
    }
  }

  // ==================== getLastByDate para IRepository ====================

  /**
   * Obtiene la última entidad por fecha de actualización
   */
  async getLastByDate(dateColumn: string = 'updated_at'): Promise<T | undefined> {
    const results = await this.db.query<T>(this.tableName, undefined, {
      column: dateColumn,
      direction: 'desc'
    });
    return results[0] || undefined;
  }

  /**
   * Igual que `getLastByDate` pero filtrando por `empresa_id`.
   *
   * Lee directo del adapter (`db.getAll`) SIN filtrar soft-deletes: si un
   * registro fue soft-deleted (is_deleted=true), su `updated_at` subió y es
   * exactamente el cursor que necesitamos para que el sync incremental traiga
   * esa eliminación. Filtrarlo dejaría el delete atrapado en el caché local.
   *
   * Usado por SyncManager.reloadAfterCompanySwitch para decidir si hace fetch
   * completo o incremental por empresa al cambiar de empresa activa. Sin este
   * filtro por empresa, el cursor global (de cualquier empresa en caché) haría
   * que el `.gt('updated_at')` pierda registros de la empresa nueva.
   */
  async getLastByDateForEmpresa(
    empresaId: string,
    dateColumn: string = 'updated_at',
  ): Promise<T | undefined> {
    const all = await this.db.getAll<T>(this.tableName);
    const filtered = (all as Record<string, unknown>[]).filter(
      (rec) => (rec as Record<string, unknown>)?.empresa_id === empresaId,
    );
    if (filtered.length === 0) return undefined;

    return filtered.reduce((latest, current) => {
      const latestDate = (latest as Record<string, unknown>)?.[dateColumn] as string || '';
      const currentDate = (current as Record<string, unknown>)?.[dateColumn] as string || '';
      return currentDate > latestDate ? current : latest;
    }) as T;
  }

  // ==================== Métodos Abstractos ====================

  /**
   * Agrega una nueva entidad
   */
  abstract add(data: Omit<T, 'id'>): Promise<string>;

  /**
   * Actualiza una entidad existente
   */
  abstract update(data: Partial<T> & { id: TId }): Promise<void>;

  /**
   * Elimina una entidad
   */
  abstract delete(id: TId): Promise<void>;

  // ==================== Soft Delete (opcional) ====================

  /**
   * Marca una entidad como eliminada lógicamente (soft delete)
   * Solo funciona si supportsSoftDelete es true
   */
  async softDelete(id: TId): Promise<void> {
    if (!this.supportsSoftDelete) {
      throw new Error(`Repository ${this.tableName} does not support soft delete. Set supportsSoftDelete=true to enable.`);
    }

    const now = this.getCurrentTimestamp();
    const updatedData: any = {
      id,
      is_deleted: true,
      updated_at: now
    };
    await this.db.update(this.tableName, id as string, updatedData);
    this.emitSync('SOFT_DELETE', updatedData);
  }

  /**
   * Restaura una entidad marcada como eliminada
   * Solo funciona si supportsSoftDelete es true
   */
  async restore(id: TId): Promise<void> {
    if (!this.supportsSoftDelete) {
      throw new Error(`Repository ${this.tableName} does not support soft delete. Set supportsSoftDelete=true to enable.`);
    }

    const updatedData: any = { id, is_deleted: false };
    await this.db.update(this.tableName, id as string, updatedData);
    this.emitSync('RESTORE', updatedData as any);
  }

  /**
   * Obtiene todas las entidades marcadas como eliminadas
   * Solo funciona si supportsSoftDelete es true
   */
  async getDeleted(): Promise<T[]> {
    if (!this.supportsSoftDelete) {
      throw new Error(`Repository ${this.tableName} does not support soft delete. Set supportsSoftDelete=true to enable.`);
    }

    return this.db.query<T>(this.tableName, {
      field: 'is_deleted',
      operator: 'equals',
      value: true
    });
  }

  // ==================== Helpers Compartidos ====================

  /**
   * Genera un UUID v4
   */
  protected generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Obtiene el timestamp actual en formato ISO
   */
  protected getCurrentTimestamp(): string {
    return new Date().toISOString();
  }

  /**
   * Obtiene el perfil actual del usuario (con caché)
   *
   * NOTA: Este método asume que el profileRepository está disponible.
   * Los repositorios que necesiten el perfil deben inyectarlo o sobreescribir este método.
   */
  protected async getCurrentProfile(): Promise<{ user_id?: string; empresa_activa_id?: string } | null> {
    const now = Date.now();

    // Retornar caché compartido si es válido
    if (BaseRepository.sharedProfileCache && (now - BaseRepository.sharedProfileCacheTime) < BaseRepository.PROFILE_CACHE_TTL) {
      return BaseRepository.sharedProfileCache;
    }

    // Intentar obtener del adapter de perfiles si está disponible
    // NOTA: Esta implementación es un placeholder. Cada repositorio que necesite
    // el perfil debe proporcionar su propia forma de obtenerlo.
    BaseRepository.sharedProfileCache = null;
    BaseRepository.sharedProfileCacheTime = now;

    return BaseRepository.sharedProfileCache;
  }

  /**
   * Establece el repositorio de perfiles para obtener datos del usuario actual
   *
   * Los repositorios pueden llamar a este método al iniciarse para poder
   * obtener empresa_id y user_id del perfil actual.
   */
  protected setProfileRepository(profileRepo: IProfileRepository): void {
    this.getCurrentProfile = async () => {
      const now = Date.now();

      if (BaseRepository.sharedProfileCache && (now - BaseRepository.sharedProfileCacheTime) < BaseRepository.PROFILE_CACHE_TTL) {
        return BaseRepository.sharedProfileCache;
      }

      try {
        const profiles = await profileRepo.getCurrentProfile();
        BaseRepository.sharedProfileCache = profiles;
        BaseRepository.sharedProfileCacheTime = now;
        return BaseRepository.sharedProfileCache;
      } catch (error) {
        console.warn(`[BaseRepository] Error getting profile:`, error);
        return null;
      }
    };
  }
}
